// src/components/appointments/AppointmentDetailShadcn.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  User,
  Mail,
  Phone,
  Edit
} from 'lucide-react';

// Import shadcn components
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Skeleton } from '../ui/skeleton';

// Import existing components
import PageContainer from '../ui/layout/PageContainer/PageContainer';
import AppointmentStatusPanelShadcn from '../ui/cards/AppointmentStatusPanel/AppointmentStatusPanelShadcn';

// Import contexts
import { useEvents } from '../../hooks/useEvents';
import { useCustomers } from '../../hooks/useCustomers';

const AppointmentDetailShadcn = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  // Events context for appointment data
  const {
    selectedEvent,
    selectedEventLoading,
    selectedEventError,
    loadEventDetails,
    updateEvent,
    deleteEvent,
    updateEventStatus,
    clearSelectedEvent,
    getEventById,
    isConnected
  } = useEvents();

  // Customers context for customer data
  const { getCustomerById } = useCustomers();

  // Local state for UI concerns
  const [saving, setSaving] = useState(false);

  // Get appointment from cache or selectedEvent
  const appointment = React.useMemo(() => {
    if (selectedEvent && selectedEvent.id === appointmentId) {
      return selectedEvent;
    }
    const cachedEvent = getEventById(appointmentId);
    return cachedEvent;
  }, [selectedEvent, appointmentId, getEventById]);

  // Get customer data
  const customer = React.useMemo(() => {
    if (appointment?.customer_id) {
      return getCustomerById(appointment.customer_id);
    }
    return null;
  }, [appointment?.customer_id, getCustomerById]);

  // Load appointment details on mount
  useEffect(() => {
    if (appointmentId && !appointment) {
      loadEventDetails(appointmentId);
    }
  }, [appointmentId, appointment, loadEventDetails]);

  // Enhanced appointment data with customer info
  const appointmentData = React.useMemo(() => {
    if (!appointment) return null;

    return {
      id: appointment.id,
      title: appointment.title,
      date: appointment.scheduled_datetime ?
        new Date(appointment.scheduled_datetime).toLocaleDateString() :
        'Date TBD',
      time: appointment.scheduled_datetime ?
        new Date(appointment.scheduled_datetime).toLocaleTimeString() :
        'Time TBD',
      duration: appointment.duration_minutes || 180,
      serviceType: appointment.service_type || appointment.title,
      address: appointment.service_address || '',
      status: appointment.status || 'pending',
      jobStatus: appointment.job_status || 'pending',
      description: appointment.description || '',
      customer: customer ? {
        id: customer.id,
        name: customer.name || customer.full_name || customer.customer_name,
        email: customer.email || customer.email_address,
        phone: customer.phone || customer.phone_number || customer.mobile,
        address: customer.address
      } : null,
      ...appointment
    };
  }, [appointment, customer]);

  // Helper to get initials
  const getInitials = (name) => {
    if (!name) return 'UC';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Navigation handlers
  const handleBack = () => {
    clearSelectedEvent();
    navigate('/appointments');
  };

  // Appointment operations
  const handleEdit = () => {
    console.log('Edit appointment:', appointmentId);
    // TODO: Implement edit functionality
  };

  const handleSaveDetails = async (updatedData) => {
    try {
      setSaving(true);
      await updateEvent(appointmentId, updatedData);
    } catch (error) {
      console.error('Failed to update appointment:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async (newStatus, statusData = {}) => {
    try {
      setSaving(true);
      await updateEventStatus(appointmentId, newStatus, statusData);
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      try {
        setSaving(true);
        await updateEventStatus(appointmentId, 'cancelled');
        navigate('/appointments');
      } catch (error) {
        console.error('Failed to cancel appointment:', error);
        setSaving(false);
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this appointment? This action cannot be undone.')) {
      try {
        setSaving(true);
        await deleteEvent(appointmentId);
        navigate('/appointments');
      } catch (error) {
        console.error('Failed to delete appointment:', error);
        setSaving(false);
      }
    }
  };

  // Loading state
  if (selectedEventLoading || !appointmentData) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading appointment details...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Error state
  if (selectedEventError) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{selectedEventError}</AlertDescription>
          </Alert>
          <Button onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Return to Appointments
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={handleBack}
        className="mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Appointments
      </Button>

      {/* Saving indicator */}
      {saving && (
        <Alert className="mb-6">
          <AlertDescription>Saving changes...</AlertDescription>
        </Alert>
      )}

      {/* Error display */}
      {selectedEventError && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{selectedEventError}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Information Card */}
          {appointmentData.customer && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarFallback className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 text-blue-400 text-lg">
                      {getInitials(appointmentData.customer.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-3">
                    <div>
                      <h3 className="text-lg font-semibold">{appointmentData.customer.name}</h3>
                    </div>
                    {appointmentData.customer.email && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="w-4 h-4" />
                        <span>{appointmentData.customer.email}</span>
                      </div>
                    )}
                    {appointmentData.customer.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="w-4 h-4" />
                        <span>{appointmentData.customer.phone}</span>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/customers/${appointmentData.customer.id}`)}
                    >
                      View Customer Profile
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Appointment Details Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Appointment Details
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={handleEdit}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Service</label>
                <p className="text-lg">{appointmentData.serviceType}</p>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Date
                  </label>
                  <p>{appointmentData.date}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Time
                  </label>
                  <p>{appointmentData.time}</p>
                </div>
              </div>

              {appointmentData.address && (
                <>
                  <Separator />
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Location
                    </label>
                    <p>{appointmentData.address}</p>
                  </div>
                </>
              )}

              {appointmentData.description && (
                <>
                  <Separator />
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Description</label>
                    <p className="text-muted-foreground">{appointmentData.description}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Right Column */}
        <div className="space-y-6">
          {/* Status Panel */}
          <AppointmentStatusPanelShadcn
            appointment={appointmentData}
            onStatusUpdate={handleStatusUpdate}
            onCancel={handleCancel}
            onDelete={handleDelete}
            loading={saving}
          />

          {/* Connection Status */}
          {!isConnected && (
            <Alert className="text-sm">
              <AlertDescription>Reconnecting to server...</AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default AppointmentDetailShadcn;
